import React, { useState } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { BiPlus, BiX } from 'react-icons/bi';
import { AccessMode, TemplateStatus, SUPPORTED_LANGUAGES } from '../../types/template';

interface Props {
    name: string;
    description: string;
    status: TemplateStatus;
    openDate: string;
    closeDate: string;
    allowEditResponse: boolean;
    notifyOnPublish: boolean;
    notifyOnSubmit: boolean;
    accessMode: AccessMode;
    languages: string[];
    names: Record<string, string>;
    descriptions: Record<string, string>;
    onLanguagesChange: (langs: string[]) => void;
    onNamesChange: (names: Record<string, string>) => void;
    onDescriptionsChange: (descs: Record<string, string>) => void;
    onChange: (patch: Partial<{
        name: string;
        description: string;
        status: TemplateStatus;
        openDate: string;
        closeDate: string;
        allowEditResponse: boolean;
        notifyOnPublish: boolean;
        notifyOnSubmit: boolean;
        accessMode: AccessMode;
    }>) => void;
}

export default function FormSettings({
    name, description,
    status, openDate, closeDate,
    allowEditResponse, notifyOnPublish, notifyOnSubmit,
    accessMode,
    languages, names, descriptions,
    onLanguagesChange, onNamesChange, onDescriptionsChange,
    onChange,
}: Props) {
    const [showLangPicker, setShowLangPicker] = useState(false);

    const addLanguage = (code: string) => {
        if (!languages.includes(code)) onLanguagesChange([...languages, code]);
        setShowLangPicker(false);
    };

    const removeLanguage = (code: string) => {
        if (languages.length <= 1) return;
        onLanguagesChange(languages.filter(l => l !== code));
    };

    const available = SUPPORTED_LANGUAGES.filter(l => !languages.includes(l.code));
    const langName = (code: string) => SUPPORTED_LANGUAGES.find(l => l.code === code)?.name ?? code.toUpperCase();

    return (
        <div className="p-4" style={{ maxWidth: 600 }}>
            {/* Languages section */}
            <fieldset className="mb-4">
                <legend className="fw-semibold fs-6 mb-2">Languages</legend>
                <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                    {languages.map(l => (
                        <span key={l} className="lang-badge">
                            {langName(l)}
                            {languages.length > 1 && (
                                <button type="button" className="lang-badge__remove" onClick={() => removeLanguage(l)} title={`Remove ${langName(l)}`}>
                                    <BiX size={12} />
                                </button>
                            )}
                        </span>
                    ))}
                    {available.length > 0 && (
                        <div style={{ position: 'relative' }}>
                            <Button variant="outline-secondary" size="sm" onClick={() => setShowLangPicker(v => !v)}>
                                <BiPlus size={14} /> Add Language
                            </Button>
                            {showLangPicker && (
                                <div className="lang-picker-dropdown">
                                    {available.map(l => (
                                        <button key={l.code} type="button" className="lang-picker-dropdown__item" onClick={() => addLanguage(l.code)}>
                                            {l.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <Form.Text className="text-muted">Forms with multiple languages show a language toggle to respondents.</Form.Text>
            </fieldset>

            {/* Name — one input per language */}
            <fieldset className="mb-4">
                <legend className="fw-semibold fs-6 mb-2">Form Name <span className="text-danger">*</span></legend>
                {languages.map(l => (
                    <Form.Group key={l} className="mb-2">
                        {languages.length > 1 && <Form.Label className="text-muted small mb-1">{langName(l)}</Form.Label>}
                        <Form.Control
                            value={names[l] ?? (l === 'en' ? name : '')}
                            onChange={e => {
                                onNamesChange({ ...names, [l]: e.target.value });
                                if (l === 'en') onChange({ name: e.target.value });
                            }}
                            placeholder={`Form name in ${langName(l)}`}
                        />
                    </Form.Group>
                ))}
            </fieldset>

            {/* Description — one input per language */}
            <fieldset className="mb-4">
                <legend className="fw-semibold fs-6 mb-2">Description <span className="text-muted fw-normal">(optional)</span></legend>
                {languages.map(l => (
                    <Form.Group key={l} className="mb-2">
                        {languages.length > 1 && <Form.Label className="text-muted small mb-1">{langName(l)}</Form.Label>}
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={descriptions[l] ?? (l === 'en' ? description : '')}
                            onChange={e => {
                                onDescriptionsChange({ ...descriptions, [l]: e.target.value });
                                if (l === 'en') onChange({ description: e.target.value });
                            }}
                            placeholder={`Description in ${langName(l)}`}
                        />
                    </Form.Group>
                ))}
            </fieldset>

            {/* Status */}
            <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">Status</Form.Label>
                <Form.Select
                    value={status}
                    onChange={e => onChange({ status: e.target.value as TemplateStatus })}
                >
                    <option value="Draft">Draft</option>
                    <option value="Published">Published</option>
                    <option value="Closed">Closed</option>
                </Form.Select>
                <Form.Text className="text-muted">
                    Set to <strong>Published</strong> to make the form available to respondents.
                </Form.Text>
            </Form.Group>

            {/* Access Mode */}
            <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">Who can fill this form?</Form.Label>
                {([
                    { value: 'InvitedOnly',  label: 'Invited only',   desc: 'Must be in the Respondents list' },
                    { value: 'Organization', label: 'ALL Employee Vietnam', desc: 'Anyone signed in with a company account' },
                    { value: 'Anonymous',    label: 'Anonymous',      desc: 'Anyone with the link — identity not tracked' },
                ] as const).map(opt => (
                    <div key={opt.value} className="form-check mb-2">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="accessMode"
                            id={`am-${opt.value}`}
                            value={opt.value}
                            checked={accessMode === opt.value}
                            onChange={() => {
                        const patch: Parameters<typeof onChange>[0] = { accessMode: opt.value };
                        if (opt.value === 'Anonymous') {
                            patch.allowEditResponse = false;
                            patch.notifyOnPublish   = false;
                            patch.notifyOnSubmit    = false;
                        }
                        onChange(patch);
                    }}
                        />
                        <label className="form-check-label" htmlFor={`am-${opt.value}`}>
                            <span className="fw-medium">{opt.label}</span>
                            <span className="text-muted ms-2" style={{ fontSize: 12 }}>{opt.desc}</span>
                        </label>
                    </div>
                ))}
            </Form.Group>

            {/* Schedule */}
            <fieldset className="mb-4">
                <legend className="fw-semibold fs-6 mb-3">Response Window (optional)</legend>
                <Row>
                    <Col sm={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Open Date &amp; Time</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={openDate}
                                onChange={e => onChange({ openDate: e.target.value })}
                            />
                        </Form.Group>
                    </Col>
                    <Col sm={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Close Date &amp; Time</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={closeDate}
                                onChange={e => onChange({ closeDate: e.target.value })}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Text className="text-muted">
                    Leave blank for always-open. Times are in UTC.
                </Form.Text>
            </fieldset>

            {/* Toggles */}
            <fieldset>
                <legend className="fw-semibold fs-6 mb-3">Behaviour</legend>

                <div className={`mb-3${accessMode === 'Anonymous' ? ' opacity-50' : ''}`}>
                    <Form.Check
                        type="switch"
                        id="toggle-edit"
                        label="Allow respondents to edit their submitted answers"
                        checked={allowEditResponse}
                        disabled={accessMode === 'Anonymous'}
                        onChange={e => onChange({ allowEditResponse: e.target.checked })}
                    />
                    {accessMode === 'Anonymous' && (
                        <Form.Text className="text-muted ms-5">
                            Not available for Anonymous forms — identity cannot be tracked.
                        </Form.Text>
                    )}
                </div>

                <div className={`mb-3${accessMode === 'Anonymous' ? ' opacity-50' : ''}`}>
                    <Form.Check
                        type="switch"
                        id="toggle-notify-publish"
                        label="Send email invite to respondents when form is Published"
                        checked={notifyOnPublish}
                        disabled={accessMode === 'Anonymous'}
                        onChange={e => onChange({ notifyOnPublish: e.target.checked })}
                    />
                    {accessMode === 'Anonymous' && (
                        <Form.Text className="text-muted ms-5">
                            Not available for Anonymous forms — no email address is collected.
                        </Form.Text>
                    )}
                </div>

            </fieldset>
        </div>
    );
}
